import { useState, useEffect, useCallback } from 'react';
import { App } from 'antd';
import { Tag, TagFormData, TagQueryParams } from '../../../types/space';
import tagService from '../../../service/tagService';

interface PaginationState {
  page: number;
  limit: number;
  total: number;
}

export const useTagManagement = () => {
  const { message } = App.useApp();
  const [tags, setTags] = useState<Tag[]>([]);
  const [statistics, setStatistics] = useState<{
    total: number;
    withIcon: number;
    withColor: number;
    withDescription: number;
  }>({
    total: 0,
    withIcon: 0,
    withColor: 0,
    withDescription: 0
  });
  const [loading, setLoading] = useState(false);
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [queryParams, setQueryParams] = useState<TagQueryParams>({});
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: 10,
    total: 0
  });

  // 加载标签列表 - 修复无限循环问题
  const loadTags = useCallback(async (params?: TagQueryParams) => {
    setLoading(true);
    try {
      // 使用传入的参数或当前状态
      const currentQueryParams = params || queryParams;
      const currentPagination = {
        page: params?.page || pagination.page,
        limit: params?.pageSize || pagination.limit
      };

      const response = await tagService.getTags({
        ...currentQueryParams,
        page: currentPagination.page,
        pageSize: currentPagination.limit
      });

      if (response.success && response.data) {
        setTags(response.data);
        if (response.pagination) {
          setPagination({
            page: response.pagination.page,
            limit: response.pagination.limit,
            total: response.pagination.total
          });
        }
      } else {
        message.error(response.error || '获取标签列表失败');
      }
    } catch (error) {
      console.error('加载标签列表失败:', error);
      message.error('加载标签列表失败');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message]); // 只依赖 message，移除 queryParams 和 pagination 依赖

  // 搜索标签
  const handleSearch = useCallback((params: TagQueryParams) => {
    setQueryParams(params);
    setPagination(prev => ({ ...prev, page: 1 }));
    loadTags({ ...params, page: 1, pageSize: 10 }); // 使用固定值而不是依赖
  }, [loadTags]);

  // 创建标签
  const handleCreate = useCallback(() => {
    setEditingTag(null);
    setFormModalVisible(true);
  }, []);

  // 编辑标签
  const handleEdit = useCallback((tag: Tag) => {
    setEditingTag(tag);
    setFormModalVisible(true);
  }, []);

  // 删除标签
  const handleDelete = useCallback(async (id: string) => {
    try {
      const response = await tagService.deleteTag(id);
      if (response.success) {
        message.success('标签删除成功');
        loadTags();
      } else {
        message.error(response.error || '删除标签失败');
      }
    } catch (error) {
      console.error('删除标签失败:', error);
      message.error('删除标签失败');
    }
  }, [loadTags, message]);

  // 批量删除标签
  const handleBatchDelete = useCallback(async (ids: string[]) => {
    try {
      // 由于API没有批量删除接口，这里逐个删除
      let successCount = 0;
      for (const id of ids) {
        const response = await tagService.deleteTag(id);
        if (response.success) {
          successCount++;
        }
      }

      if (successCount === ids.length) {
        message.success(`成功删除 ${successCount} 个标签`);
      } else {
        message.warning(`成功删除 ${successCount}/${ids.length} 个标签`);
      }

      loadTags();
    } catch (error) {
      console.error('批量删除标签失败:', error);
      message.error('批量删除标签失败');
    }
  }, [loadTags, message]);

  // 表单提交成功
  const handleFormSuccess = useCallback(async (values: TagFormData): Promise<boolean> => {
    try {
      let response;
      if (editingTag) {
        response = await tagService.updateTag(editingTag.id, values);
      } else {
        response = await tagService.createTag(values);
      }

      if (response.success) {
        message.success(response.message || (editingTag ? '标签更新成功' : '标签创建成功'));
        setFormModalVisible(false);
        setEditingTag(null);
        loadTags();
        return true;
      } else {
        message.error(response.error || (editingTag ? '更新标签失败' : '创建标签失败'));
        return false;
      }
    } catch (error) {
      console.error('提交表单失败:', error);
      message.error(editingTag ? '更新标签失败' : '创建标签失败');
      return false;
    }
  }, [editingTag, loadTags, message]);

  // 分页变化
  const handlePaginationChange = useCallback((paginationConfig: any) => {
    const { current, pageSize } = paginationConfig;
    setPagination(prev => ({ ...prev, page: current || 1, limit: pageSize || 10 }));
    loadTags({ ...queryParams, page: current || 1, pageSize: pageSize || 10 });
  }, [queryParams, loadTags]);

  // 关闭表单弹窗
  const closeFormModal = useCallback(() => {
    setFormModalVisible(false);
    setEditingTag(null);
  }, []);

  // 加载统计数据
  const loadStatistics = useCallback(async () => {
    try {
      const response = await tagService.getTagStatistics();
      if (response.success && response.data) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('加载统计数据失败:', error);
    }
  }, []);

  // 初始化加载
  useEffect(() => {
    loadTags();
    loadStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    tags,
    statistics,
    loading,
    formModalVisible,
    editingTag,
    queryParams,
    pagination,
    loadTags,
    handleSearch,
    handleCreate,
    handleEdit,
    handleDelete,
    handleBatchDelete,
    handleFormSuccess,
    handlePaginationChange,
    closeFormModal
  };
};